import { Subject, Observable } from 'rxjs';
import { Injectable, ComponentRef, ViewContainerRef, Type, TemplateRef } from '@angular/core';
import { ModalSize, ModalTheme } from '@newroom-connect/library/enums';

export interface ModalConfig<T> {
  component: Type<T>;
  inputs: ModalConfigInputs;
  content?: Type<unknown> | TemplateRef<unknown>;
}

export interface ModalConfigInputs {
  id: string;
  size: ModalSize;
  title?: string;
  paddedContent?: boolean;
  theme?: ModalTheme;
  openInFullscreen?: boolean;
  [key: string]: unknown;
}

interface ModalStackItem {
  id: string;
  componentRef: ComponentRef<unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private modalStack: ModalStackItem[] = [];

  private rootViewContainer: ViewContainerRef | null = null;
  private modalClosed = new Subject<{ id: string, result: unknown }>();

  public modalClosed$: Observable<{ id: string, result?: unknown }> = this.modalClosed.asObservable();

  /**
   * Sets the root view container ref for the modal service.
   * This should be called once from the app component.
   *
   * @param viewContainerRef The ViewContainerRef to use as the root container.
   */
  public setRootViewContainerRef(viewContainerRef: ViewContainerRef): void {
    this.rootViewContainer = viewContainerRef;
  }

  /**
   * Opens a modal with the specified configuration.
   *
   * @param modalConfig
   *
   * @returns
   * @throws Error if no suitable container is found.
   */
  public open<T extends object>(modalConfig: ModalConfig<T>): ComponentRef<T>  {
    if (!this.rootViewContainer) {
      throw new Error('Root view container not set');
    }

    const componentRef = this.rootViewContainer.createComponent(modalConfig.component);

    if (modalConfig.inputs) {
      Object.assign(componentRef.instance, modalConfig.inputs);
    }

    if (modalConfig.content && 'content' in componentRef.instance) {
      componentRef.instance.content = modalConfig.content;
    }

    this.modalStack.push({ id: modalConfig.inputs.id, componentRef });

    return componentRef;
  }

  /**
   * Closes the currently open modal.
   *
   * @param modalId
   * @param result
   */
  public close(modalId: string, result?: unknown): void {
    const closedModal = this.modalStack.find(item => item.id === modalId);

    if (!closedModal) {
      return;
    }

    closedModal.componentRef.destroy();

    this.modalStack = this.modalStack.filter(item => item.id !== modalId);
    this.modalClosed.next({ id: closedModal.id, result });
  }
}
